import {
  Alert,
  Button,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import {
  useCompanies,
  useCompanyOutlets,
  useDeactivateServiceType,
  useSaveServiceType,
  useServiceTypes,
} from '@/api/hooks'
import type { ServiceTypeView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'
import { BranchLimitsDialog } from './BranchLimitsDialog'

/**
 * Where the food is served, which is the first thing that divides the trade.
 *
 * <p>Inside our premises it is served in a space of ours and there is nobody to
 * deliver to; outside it needs an address and books no space at all.</p>
 */
const PREMISES = [
  { value: 'INSIDE', label: 'Inside premise' },
  { value: 'OUTSIDE', label: 'Outside premise' },
]

const BLANK: ServiceTypeView = {
  id: '',
  code: '',
  name: '',
  spaceType: undefined,
  sacCode: '',
  needsDeliveryAddress: false,
  bookable: true,
  minPax: null,
  maxPax: null,
  sortOrder: 99,
  active: true,
  outletIds: [],
}

/**
 * The lines of business, and which outlet sells which.
 *
 * <p>These used to be four constants in the code, so a fifth meant a release
 * and every outlet implicitly offered every service - which was never true. A
 * takeaway counter has no banquet hall, and the hall does not sell over a
 * counter.</p>
 */
export function ServicesTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  // Whose services are being set up. A service belongs to a company as firmly
  // as its customers do, so a platform administrator picks the company first
  // and then maps each service to that company's outlets.
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const target = companyId ?? ownCompanyId
  const { data: companies } = useCompanies()
  const canPickCompany = can('platform.admin') && (companies ?? []).length > 1
  const { data: services, error } = useServiceTypes({ companyId, includeInactive: true })
  // That company's outlets, not the ones the person editing happens to work in.
  const { data: outlets } = useCompanyOutlets(target)
  const save = useSaveServiceType()
  const deactivate = useDeactivateServiceType()

  const [rows, setRows] = useState<ServiceTypeView[]>([])
  // The service whose branch limits are being set, or null.
  const [branchLimitsFor, setBranchLimitsFor] = useState<ServiceTypeView | null>(null)
  const writable = canWrite('config')

  useEffect(() => {
    setRows((services ?? []).map((row) => ({ ...row })))
  }, [services])

  const update = (index: number, patch: Partial<ServiceTypeView>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)))

  const nameOf = (outletId: string) => {
    const outlet = (outlets ?? []).find((one) => one.id === outletId)
    return outlet ? `${outlet.code} - ${outlet.name}` : '—'
  }

  return (
    <>
      <ErrorBanner error={error ?? save.error ?? deactivate.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {canPickCompany ? (
            <TextField
              select
              size="small"
              label="Company"
              value={target ?? ''}
              onChange={(event) => setCompanyId(event.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Company' }}
            >
              {(companies ?? []).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            What this company sells, and from which outlet. A service with no outlet ticked cannot
            be sold anywhere.
          </Typography>
        </Stack>
        {writable ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRows((current) => [...current, { ...BLANK }])}
          >
            New service
          </Button>
        ) : null}
      </Stack>

      {/* Fixed layout: the outlet list is long and was squeezing the code and
          name columns down to a single letter each. */}
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1240 }}>
        <TableHead>
          <TableRow>
            <TableCell width={160}>Code</TableCell>
            <TableCell width={200}>Name</TableCell>
            <TableCell width={200}>Premises</TableCell>
            <TableCell width={130}>SAC</TableCell>
            <TableCell width={100}>Bookable</TableCell>
            <TableCell width={280}>Outlets</TableCell>
            <TableCell align="right" width={120}>
              &nbsp;
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            // The code is what every order, package and term already names, so
            // it is fixed once the service has been sold under.
            const codeFixed = Boolean(row.id)
            return (
              <TableRow key={row.id || `new-${index}`} sx={{ opacity: row.active ? 1 : 0.55 }}>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.code}
                    disabled={!writable || codeFixed}
                    onChange={(event) =>
                      update(index, { code: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })
                    }
                    inputProps={{ 'aria-label': `Code ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.name}
                    disabled={!writable}
                    onChange={(event) => update(index, { name: event.target.value })}
                    inputProps={{ 'aria-label': `Name ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={row.needsDeliveryAddress ? 'OUTSIDE' : 'INSIDE'}
                    disabled={!writable}
                    onChange={(event) => {
                      const outside = event.target.value === 'OUTSIDE'
                      // Food that leaves cannot also be booked into a room of
                      // ours, so the space goes with the choice.
                      update(index, {
                        needsDeliveryAddress: outside,
                        // The room a service books is set up with the service
                        // and kept; food that leaves books none at all.
                        spaceType: outside ? undefined : row.spaceType,
                      })
                    }}
                    inputProps={{ 'aria-label': `Premises ${index + 1}` }}
                  >
                    {PREMISES.map((where) => (
                      <MenuItem key={where.value} value={where.value}>
                        {where.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>

                <TableCell>
                  <Tooltip title="What this line of business is billed under: 996334 for event catering, 996331 for a restaurant. Leave it blank where what is sold is goods, and each product's own HSN is printed instead.">
                    <TextField
                      fullWidth
                      size="small"
                      value={row.sacCode ?? ''}
                      disabled={!writable}
                      placeholder="996334"
                      onChange={(event) =>
                        update(index, { sacCode: event.target.value.replace(/[^0-9]/g, '') })
                      }
                      inputProps={{ 'aria-label': `SAC ${index + 1}`, maxLength: 8 }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Off for a counter sale: nothing to quote, staff or hold a hall for">
                    <Switch
                      size="small"
                      checked={row.bookable}
                      disabled={!writable}
                      onChange={(event) => update(index, { bookable: event.target.checked })}
                      inputProps={{ 'aria-label': `Bookable ${index + 1}` }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={row.outletIds}
                    disabled={!writable}
                    onChange={(event) =>
                      update(index, {
                        outletIds:
                          typeof event.target.value === 'string'
                            ? event.target.value.split(',')
                            : (event.target.value as unknown as string[]),
                      })
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) => {
                        const chosen = selected as string[]
                        if (chosen.length === 0) return 'Nowhere'
                        if (chosen.length === (outlets ?? []).length) return 'Every outlet'
                        return chosen.map(nameOf).join(', ')
                      },
                    }}
                    inputProps={{ 'aria-label': `Outlets ${index + 1}` }}
                  >
                    {(outlets ?? []).map((outlet) => (
                      <MenuItem key={outlet.id} value={outlet.id}>
                        <Checkbox size="small" checked={row.outletIds.includes(outlet.id)} />
                        <ListItemText primary={`${outlet.code} - ${outlet.name}`} />
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    {row.active ? null : <Chip size="small" label="Off" />}
                    {/* Where a branch can do more or less than the business.
                        Only once the service exists to hang them off. */}
                    {row.id ? (
                      <Tooltip title="How many, branch by branch">
                        <Button size="small" onClick={() => setBranchLimitsFor(row)}>
                          By branch
                        </Button>
                      </Tooltip>
                    ) : null}
                    {writable ? (
                      <>
                        <Button
                          size="small"
                          disabled={!row.code.trim() || !row.name.trim() || save.isPending}
                          onClick={() =>
                            save.mutate({
                              id: row.id || undefined,
                              companyId,
                              code: row.code.trim(),
                              name: row.name.trim(),
                              spaceType: row.spaceType || undefined,
                              // Sent empty rather than left out, so clearing it
                              // clears the master.
                              sacCode: (row.sacCode ?? '').trim(),
                              needsDeliveryAddress: row.needsDeliveryAddress,
                              bookable: row.bookable,
                              minPax: row.minPax,
                              maxPax: row.maxPax,
                              sortOrder: row.sortOrder,
                              active: row.active,
                              outletIds: row.outletIds,
                            })
                          }
                        >
                          Save
                        </Button>
                        {row.id && row.active ? (
                          <Tooltip title="Switch it off. What was sold under it still reads correctly.">
                            <IconButton
                              size="small"
                              onClick={() => deactivate.mutate({ id: row.id, companyId })}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Inside premise</strong> is served in a room of ours and asks for no address;{' '}
        <strong>Outside premise</strong> asks for a delivery address.{' '}
        <strong>SAC</strong> is what this line of business is billed under — 996334 for event
        catering. Left blank, every document prints each product&apos;s own HSN instead, which is
        how a counter selling goods is billed.{' '}
        Whether it asks what occasion it is for is set under <strong>Occasions</strong>: a line of
        business with none listed there is never asked.{' '}
        <strong>Bookable</strong> off means it is sold over the counter and never quoted, staffed or
        held against a room — a counter sale is inside the premise and still books nothing. A code
        cannot change once anything has been sold under it, because every order and package names it.
      </Alert>

      <BranchLimitsDialog
        service={branchLimitsFor}
        onClose={() => setBranchLimitsFor(null)}
      />
    </>
  )
}
